// HealingTool — overlay-komponent som tar över Detail-stage:n när
// användaren går in i edit-mode.
//
// Flow:
//   1. Mount → ladda FULL-RES-bilden (5040px) i en HTMLImageElement.
//      Healing sker på riktig pixel-data, inte uppskalad preview.
//   2. Parallellt: ladda OpenCV.js (lazy script-tag).
//   3. När båda redo: rita bilden på en canvas. Visa brush-cursor.
//   4. Klick / drag → bygg en mask-canvas i samma storlek (vita prickar
//      där användaren markerat).
//   5. "Apply" → kör cv.inpaint på source + mask, ersätt source canvas.
//   6. "Reset Apply" / undo-stack låter ångra senaste inpaint.
//   7. "Save" → canvas.toBlob('image/jpeg', 0.92) → POST till backend.
//
// Inga manuella AnimationControllers, inga regex. Tailwind klasser.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Brush, Loader2, RotateCcw, Save, X, Eye } from 'lucide-react';
import { loadOpenCV, isOpenCVReady } from '../../lib/opencv-loader';
import { inpaintCanvas } from '../../lib/inpaint';
import { createEdit } from '../../api/edits';

// ─── Types ─────────────────────────────────────────────────────

interface HealingToolProps {
  imageId: string;
  /** URL till bilden vi editerar — bör vara full-res (5040px) så
   *  inpaint sker på faktisk pixel-data, inte uppskalad preview.  */
  src: string;
  /** Bildens naturliga dimensioner (för aspect ratio + canvas-storlek). */
  naturalWidth?: number;
  naturalHeight?: number;
  /** Anropas vid Cancel eller efter lyckad Save. */
  onClose: () => void;
  /** Anropas efter en sparad edit så parent kan invalidate caches. */
  onSaved?: (editId: string) => void;
}

interface MaskStroke {
  // En "stroke" är antingen en enda klickprick (start === end) eller en
  // dragad linje med flera punkter. Vi sparar punkter för undo + framtida
  // server-side re-rendering på full upplösning.
  points: { x: number; y: number }[];
  radius: number;
}

type Stage = 'loading' | 'ready' | 'inpainting' | 'saving' | 'error';

// ─── Konstanter ────────────────────────────────────────────────

const DEFAULT_BRUSH = 30;
const MIN_BRUSH = 5;
const MAX_BRUSH = 200;
// Solid täckande röd istället för halv-transparent — då ser man tydligt vad
// som markerats även när flera strokes överlappar. Vi använder ändå alpha
// för att se bilden bakom (men låg alpha additiv blending blev förvirrande).
const MASK_OVERLAY_RGBA = 'rgba(255, 70, 70, 0.65)';

// ─── Komponent ─────────────────────────────────────────────────

export function HealingTool({
  imageId,
  src,
  naturalWidth,
  naturalHeight,
  onClose,
  onSaved,
}: HealingToolProps) {
  // Refs för canvas-elementen. Tre lager:
  //  - sourceCanvas: bilden själv (uppdateras efter inpaint)
  //  - maskCanvas: vit-på-svart binär mask, samma storlek som källan (offscreen)
  //  - overlayCanvas: visuell röd halv-transparent overlay för markerade områden
  //  - cursorCanvas: brush-preview-cirkel som följer musen
  const containerRef = useRef<HTMLDivElement>(null);
  const sourceCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const cursorCanvasRef = useRef<HTMLCanvasElement>(null);
  // Mask-canvas hålls offscreen — den är källan vi skickar till cv.inpaint.
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Backup av sourceCanvas:n från innan senaste apply, för "undo".
  const undoStackRef = useRef<ImageData[]>([]);

  // State som driver UI:t.
  const [stage, setStage] = useState<Stage>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH);
  const [strokes, setStrokes] = useState<MaskStroke[]>([]);
  const [showOriginal, setShowOriginal] = useState(false);
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  // Image-element för originalet — vi återanvänder vid showOriginal-toggle.
  const [naturalSize, setNaturalSize] = useState({
    w: naturalWidth ?? 0,
    h: naturalHeight ?? 0,
  });
  // Display-storlek — hur stor canvas:n RENDERAS (CSS pixels). Skiljer sig
  // från naturalSize (där pixlarna ligger) — vi vill rita på native-pixlar
  // för bästa kvalitet men visa fit-to-screen.
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  const opencvReady = useRef(isOpenCVReady());
  const drawingRef = useRef<{ pointerId: number | null; current: MaskStroke | null }>({
    pointerId: null,
    current: null,
  });

  // Zoom + pan state. Zoom är multiplikator (1 = fit-to-screen, 2 = 2× osv).
  // Pan är offset i CSS-pixlar från center.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // True när space-bar håller eller middle-mouse triggar pan-läge.
  const [panMode, setPanMode] = useState(false);
  const panDragRef = useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  }>({ pointerId: null, startX: 0, startY: 0, panX: 0, panY: 0 });

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 8;

  // ── Ladda bild + OpenCV ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setStage('loading');
    setErrorMsg(null);

    const img = new Image();
    // Vi laddar same-origin (Loupe API). crossOrigin='anonymous' triggar CORS-
    // krav även same-origin → bilden laddas men canvas blir "tainted" och blir
    // helt svart. Lämnar attributet ej satt för same-origin (default beteende).
    img.decoding = 'async';
    img.src = src;

    const cvPromise = loadOpenCV().catch((err: Error) => {
      // Behåll error för UI; men låt bilden ändå rendera så användaren ser
      // VAD hen är på väg att editera (och vi kan visa "Editor failed to load").
      throw err;
    });

    Promise.all([
      img.decode().then(() => img),
      cvPromise,
    ])
      .then(([loadedImg]) => {
        if (cancelled) return;
        const w = loadedImg.naturalWidth || naturalWidth || 0;
        const h = loadedImg.naturalHeight || naturalHeight || 0;
        setNaturalSize({ w, h });
        setOriginalImage(loadedImg);
        opencvReady.current = true;

        // Initiera offscreen mask-canvas (svart bakgrund, samma storlek).
        const mask = document.createElement('canvas');
        mask.width = w;
        mask.height = h;
        const mctx = mask.getContext('2d');
        if (mctx) {
          mctx.fillStyle = '#000';
          mctx.fillRect(0, 0, w, h);
        }
        maskCanvasRef.current = mask;

        // OBS: Vi ritar INTE source-canvas här. Den existerar inte i DOM ännu
        // (stage='loading' visar bara loader). Bilden ritas i en separat
        // useEffect som triggar när stage='ready' OCH originalImage är satt.

        setStage('ready');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setErrorMsg(err.message || 'Failed to load editor');
        setStage('error');
      });

    return () => {
      cancelled = true;
    };
  }, [src, naturalWidth, naturalHeight]);

  // Rita källbilden på source-canvas när den blir tillgänglig i DOM.
  //
  // KRITISKT: vi får INTE rensa canvasen via canvas.width-tilldelning på
  // varje re-render, för det skulle radera healing-resultatet. Vi sätter
  // dimensionerna ENDAST när de skiljer sig från nuvarande, OCH ritar
  // bilden ENDAST om canvasen är tom (eller pekar på annan canvas-instans).
  const initializedCanvas = useRef<HTMLCanvasElement | null>(null);
  useLayoutEffect(() => {
    if (stage !== 'ready' || !originalImage || !naturalSize.w) return;
    const canvas = sourceCanvasRef.current;
    if (!canvas) return;

    // Om samma canvas-instans redan är initialiserad, gör ingenting.
    // Healing skriver direkt till denna canvas — rör vi den raderas resultatet.
    if (initializedCanvas.current === canvas) return;

    // Sätt dimensioner endast om de inte redan är rätt (annars rensas pixlar).
    if (canvas.width !== naturalSize.w) canvas.width = naturalSize.w;
    if (canvas.height !== naturalSize.h) canvas.height = naturalSize.h;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(originalImage, 0, 0);

    // Initiera även overlay-canvas storlek (samma logik)
    const overlay = overlayCanvasRef.current;
    if (overlay) {
      if (overlay.width !== naturalSize.w) overlay.width = naturalSize.w;
      if (overlay.height !== naturalSize.h) overlay.height = naturalSize.h;
    }

    initializedCanvas.current = canvas;
  }, [stage, originalImage, naturalSize.w, naturalSize.h]);

  // ── Mät display-storlek (CSS pixels) ─────────────────────────
  // Vi vill att canvas:n fyller containern fit-to-contain. Naturlig storlek
  // är intern pixel-resolution; vi sätter CSS width/height för rendering.
  useLayoutEffect(() => {
    if (!containerRef.current || !naturalSize.w || !naturalSize.h) return;
    const ro = new ResizeObserver(() => updateDisplaySize());
    ro.observe(containerRef.current);
    updateDisplaySize();
    return () => ro.disconnect();

    function updateDisplaySize() {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;
      if (cw <= 0 || ch <= 0) return;
      const naturalAspect = naturalSize.w / naturalSize.h;
      const containerAspect = cw / ch;
      let dw, dh;
      if (naturalAspect > containerAspect) {
        dw = cw;
        dh = cw / naturalAspect;
      } else {
        dh = ch;
        dw = ch * naturalAspect;
      }
      setDisplaySize({ w: Math.floor(dw), h: Math.floor(dh) });
    }
  }, [naturalSize]);

  // ── Cursor canvas storlek matchar STAGE-containern (hela vyn) ─
  // Cursor-canvasen ligger nu utanför displaySize-rutan och täcker hela
  // stage-containern. Dess pixel-buffer måste matcha sin CSS-storlek
  // exakt — annars blir brush-cirkeln deformerad/oval. Vi använder
  // ResizeObserver så vi följer container-storleken.
  useEffect(() => {
    const c = cursorCanvasRef.current;
    const container = containerRef.current;
    if (!c || !container) return;
    const dpr = window.devicePixelRatio || 1;
    function resize() {
      if (!c || !container) return;
      const rect = container.getBoundingClientRect();
      c.width = Math.floor(rect.width * dpr);
      c.height = Math.floor(rect.height * dpr);
      const ctx = c.getContext('2d');
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0); // reset eventuellt tidigare scale
        ctx.scale(dpr, dpr);
      }
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, [stage, panMode]);

  // ── Synkron mask + overlay-rendering ─────────────────────────
  // Helper som ritar både mask-canvasen (för cv.inpaint) och overlay-canvasen
  // (för UI). Synkron så vi kan kalla den direkt i pointerUp innan apply.
  const renderMaskFromStrokes = useCallback((strokesArr: MaskStroke[]) => {
    const overlay = overlayCanvasRef.current;
    const mask = maskCanvasRef.current;
    if (!overlay || !mask) return;
    const octx = overlay.getContext('2d');
    const mctx = mask.getContext('2d');
    if (!octx || !mctx) return;

    // ── MASK (för OpenCV inpaint): svart bg + vita strokes ──
    mctx.globalCompositeOperation = 'source-over';
    mctx.fillStyle = '#000';
    mctx.fillRect(0, 0, mask.width, mask.height);
    mctx.fillStyle = '#fff';
    mctx.strokeStyle = '#fff';
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';

    // ── OVERLAY (UI): transparent bg + röda strokes ──
    octx.globalCompositeOperation = 'source-over';
    octx.clearRect(0, 0, overlay.width, overlay.height);
    octx.fillStyle = MASK_OVERLAY_RGBA;
    octx.strokeStyle = MASK_OVERLAY_RGBA;
    octx.lineCap = 'round';
    octx.lineJoin = 'round';

    for (const stroke of strokesArr) {
      const r = stroke.radius;
      if (stroke.points.length === 1) {
        const p = stroke.points[0];
        if (!p) continue;
        mctx.beginPath();
        mctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        mctx.fill();
        octx.beginPath();
        octx.arc(p.x, p.y, r, 0, Math.PI * 2);
        octx.fill();
      } else {
        mctx.lineWidth = r * 2;
        octx.lineWidth = r * 2;
        mctx.beginPath();
        octx.beginPath();
        for (let i = 0; i < stroke.points.length; i++) {
          const p = stroke.points[i];
          if (!p) continue;
          if (i === 0) {
            mctx.moveTo(p.x, p.y);
            octx.moveTo(p.x, p.y);
          } else {
            mctx.lineTo(p.x, p.y);
            octx.lineTo(p.x, p.y);
          }
        }
        mctx.stroke();
        octx.stroke();
      }
    }
  }, []);

  // Trigger render vid varje strokes-uppdatering (för att overlay ska visas
  // medan användaren drar). pointerUp kör en synkron extra-render direkt.
  useEffect(() => {
    renderMaskFromStrokes(strokes);
  }, [strokes, renderMaskFromStrokes]);

  // ── Map: client coords → source-canvas coords ────────────────
  // sourceCanvas ligger inuti en transformerad div (zoom + pan via CSS).
  // getBoundingClientRect() returnerar redan post-transform-koordinater,
  // så samma formel funkar oavsett zoom: vi normaliserar mot rect och
  // skalar upp till naturalSize.
  const clientToSource = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const c = sourceCanvasRef.current;
      if (!c || displaySize.w === 0) return null;
      const rect = c.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      const x = ((clientX - rect.left) / rect.width) * naturalSize.w;
      const y = ((clientY - rect.top) / rect.height) * naturalSize.h;
      return {
        x: Math.max(0, Math.min(naturalSize.w, x)),
        y: Math.max(0, Math.min(naturalSize.h, y)),
      };
    },
    [displaySize, naturalSize],
  );

  // Brush-storleken är i CSS-pixlar (vid zoom=1) för UX, men måste skalas till
  // source-pixlar för mask:en. Vid zoom>1 ser användaren samma fysiska brush
  // (CSS-pixlar) men den täcker mindre av source — så vi delar med zoom också.
  const sourceBrushRadius = useMemo(() => {
    if (!displaySize.w) return brushSize;
    // Effektiv display-bredd vid nuvarande zoom = displaySize.w * zoom
    const effectiveDisplayW = displaySize.w * zoom;
    const scale = naturalSize.w / effectiveDisplayW;
    return brushSize * scale;
  }, [brushSize, displaySize, naturalSize, zoom]);

  // ── Pointer handlers ─────────────────────────────────────────
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (stage !== 'ready' || showOriginal) return;
      // Pan-mode (space-bar håller eller middle-button) → drag = pan
      const isMiddleButton = e.button === 1;
      if (panMode || isMiddleButton) {
        e.preventDefault();
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
        panDragRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          panX: pan.x,
          panY: pan.y,
        };
        return;
      }
      e.preventDefault();
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      const pos = clientToSource(e.clientX, e.clientY);
      if (!pos) return;
      const stroke: MaskStroke = {
        points: [pos],
        radius: sourceBrushRadius,
      };
      drawingRef.current = { pointerId: e.pointerId, current: stroke };
      setStrokes((prev) => [...prev, stroke]);
    },
    [stage, showOriginal, clientToSource, sourceBrushRadius, panMode, pan],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Pan-drag pågår?
      const panDrag = panDragRef.current;
      if (panDrag.pointerId === e.pointerId) {
        const dx = e.clientX - panDrag.startX;
        const dy = e.clientY - panDrag.startY;
        setPan({ x: panDrag.panX + dx, y: panDrag.panY + dy });
        return;
      }
      // Cursor-rendering — alltid (även när inte aktivt drag) för brush-preview.
      drawCursor(e.clientX, e.clientY);

      const drag = drawingRef.current;
      if (drag.pointerId !== e.pointerId || !drag.current) return;
      const pos = clientToSource(e.clientX, e.clientY);
      if (!pos) return;
      const stroke = drag.current;
      // Lägg till punkten — men bara om den är "tillräckligt långt" från
      // föregående för att inte spamma identiska koordinater.
      const lastPoint = stroke.points[stroke.points.length - 1];
      if (lastPoint) {
        const dx = pos.x - lastPoint.x;
        const dy = pos.y - lastPoint.y;
        const minDelta = Math.max(2, sourceBrushRadius * 0.2);
        if (dx * dx + dy * dy < minDelta * minDelta) return;
      }
      stroke.points.push(pos);
      // Trigga re-render — vi muterar referensen och setStrokes med ny array.
      setStrokes((prev) => prev.slice());
    },
    [clientToSource, sourceBrushRadius],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      try {
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const wasDrawing = drawingRef.current.pointerId === e.pointerId;
      const finishedStroke = wasDrawing ? drawingRef.current.current : null;
      if (wasDrawing) {
        drawingRef.current = { pointerId: null, current: null };
        // Instant healing: applicera så fort användaren släpper.
        if (finishedStroke && finishedStroke.points.length > 0) {
          // KRITISKT: rita masken SYNKRONT här (inte vänta på React effect)
          // så vi vet att mask-canvasen är uppdaterad innan vi inpaintar.
          renderMaskFromStrokes(strokesRef.current);
          // Sen kör inpaint asynkront via worker.
          triggerApplyRef.current?.();
        }
      }
      if (panDragRef.current.pointerId === e.pointerId) {
        panDragRef.current = { pointerId: null, startX: 0, startY: 0, panX: 0, panY: 0 };
      }
    },
    [],
  );

  // Ref till handleApply så vi kan trigga den från onPointerUp utan att
  // skapa cirkulära dependency-issues mellan callbackerna.
  const triggerApplyRef = useRef<(() => void) | null>(null);

  const onPointerLeave = useCallback(() => {
    // Rensa cursor-cirkeln när musen lämnar canvas:n.
    const c = cursorCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx?.clearRect(0, 0, c.width, c.height);
  }, []);

  // ── Cursor-rendering ─────────────────────────────────────────
  // Cursor-canvas ligger UTANFÖR den transformerade containern (i den
  // o-transformerade pointer-rooten), så positioner och storlekar är
  // direkt i CSS-pixlar. Inga zoom-justeringar behövs.
  function drawCursor(clientX: number, clientY: number) {
    const c = cursorCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, c.width / dpr, c.height / dpr);

    const rect = c.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillStyle = 'rgba(255, 64, 64, 0.18)';
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // ── Wheel = brush size, eller Cmd/Ctrl + wheel = zoom ──────────
  // Vi binder native event listener (inte React's onWheel) så vi kan göra
  // den non-passive och faktiskt preventDefault scroll. React's wheel-events
  // är passive sedan React 17 → preventDefault() ger console-error.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function handler(e: WheelEvent) {
      if (stage !== 'ready') return;
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) {
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom((z) => {
          const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * factor));
          if (newZoom === 1) setPan({ x: 0, y: 0 });
          return newZoom;
        });
      } else {
        const delta = e.deltaY > 0 ? -2 : 2;
        setBrushSize((b) => Math.max(MIN_BRUSH, Math.min(MAX_BRUSH, b + delta)));
      }
    }
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [stage]);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Vid varje zoom-ändring: clampa pan så bilden inte kan flyttas helt
  // utanför vyn. Vid zoom=1 ska pan vara 0 (centerad). Vid zoom>1 får
  // pan max vara halva extra-bredden i varje riktning.
  useEffect(() => {
    if (zoom === 1) {
      if (pan.x !== 0 || pan.y !== 0) setPan({ x: 0, y: 0 });
      return;
    }
    if (!displaySize.w || !displaySize.h) return;
    // Maximal pan = (zoomad bredd - basbredd) / 2 = displaySize.w * (zoom - 1) / 2
    const maxX = (displaySize.w * (zoom - 1)) / 2;
    const maxY = (displaySize.h * (zoom - 1)) / 2;
    const clampedX = Math.max(-maxX, Math.min(maxX, pan.x));
    const clampedY = Math.max(-maxY, Math.min(maxY, pan.y));
    if (clampedX !== pan.x || clampedY !== pan.y) {
      setPan({ x: clampedX, y: clampedY });
    }
  }, [zoom, displaySize, pan]);

  // ── Apply (inpaint) ──────────────────────────────────────────
  const handleApply = useCallback(async () => {
    if (stage !== 'ready') return;
    const source = sourceCanvasRef.current;
    const mask = maskCanvasRef.current;
    if (!source || !mask) return;
    if (strokesRef.current.length === 0) return;

    setStage('inpainting');
    try {
      const ctx = source.getContext('2d');
      if (!ctx) throw new Error('Could not get source canvas 2d context');

      // Snapshot för undo
      const snap = ctx.getImageData(0, 0, source.width, source.height);

      const result = await inpaintCanvas(source, mask, { inpaintRadius: 5 });

      undoStackRef.current.push(snap);
      if (undoStackRef.current.length > 10) undoStackRef.current.shift();

      // Skriv resultatet till source-canvas
      ctx.clearRect(0, 0, source.width, source.height);
      ctx.drawImage(result.canvas, 0, 0);

      // Rensa strokes
      setStrokes([]);
      strokesRef.current = [];
      // Rensa overlay direkt (annars syns röd mask kvar tills nästa render)
      renderMaskFromStrokes([]);

      setStage('ready');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Inpaint failed';
      console.error('[Loupe Healing] Inpaint error:', err);
      setErrorMsg(msg);
      setStrokes([]);
      strokesRef.current = [];
      renderMaskFromStrokes([]);
      setStage('ready');
    }
  }, [stage, renderMaskFromStrokes]);

  // Synca handleApply till triggerApplyRef så onPointerUp kan kalla den
  // utan att fastna i closure med stale state.
  useEffect(() => {
    triggerApplyRef.current = handleApply;
  }, [handleApply]);

  // Spegla strokes till en ref så onPointerUp kan kolla utan re-render-race.
  const strokesRef = useRef<MaskStroke[]>([]);
  useEffect(() => {
    strokesRef.current = strokes;
  }, [strokes]);

  // ── Undo last apply ──────────────────────────────────────────
  const handleUndoApply = useCallback(() => {
    const source = sourceCanvasRef.current;
    if (!source) return;
    const snap = undoStackRef.current.pop();
    if (!snap) return;
    const ctx = source.getContext('2d');
    ctx?.putImageData(snap, 0, 0);
  }, []);

  // (Tidigare handleUndoStroke och handleClearMarks är borttagna eftersom
  //  strokes appliceras direkt vid pointer-up — det finns inte längre någon
  //  "pending stroke"-state att rensa eller ångra. Cmd+Z ångrar nu alltid
  //  senaste applied heal.)

  // ── Save ─────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const source = sourceCanvasRef.current;
    if (!source) return;
    if (undoStackRef.current.length === 0 && strokes.length === 0) {
      // Inget faktiskt applicerat — annulera istället för att posta ett
      // identiskt original.
      onClose();
      return;
    }
    setStage('saving');
    try {
      const blob: Blob | null = await new Promise((resolve) =>
        source.toBlob((b) => resolve(b), 'image/jpeg', 0.92),
      );
      if (!blob) throw new Error('Could not encode image');
      const params = {
        sourceSize: 'preview',
        algorithm: 'TELEA',
        inpaintRadius: 3,
        appliedSteps: undoStackRef.current.length,
      };
      const edit = await createEdit(imageId, blob, { type: 'heal', params });
      onSaved?.(edit.id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setErrorMsg(msg);
      setStage('ready');
    }
  }, [imageId, onClose, onSaved, strokes.length]);

  // ── Keyboard shortcuts ───────────────────────────────────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ignorera om fokus i input.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === '[') {
        e.preventDefault();
        setBrushSize((b) => Math.max(MIN_BRUSH, b - 5));
      } else if (e.key === ']') {
        e.preventDefault();
        setBrushSize((b) => Math.min(MAX_BRUSH, b + 5));
      } else if (e.key === 'f' || e.key === 'F') {
        // Före/efter-toggle.
        e.preventDefault();
        setShowOriginal((s) => !s);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) return; // Cmd+Shift+Z (redo) — inte implementerad
        handleUndoApply();
      } else if (e.key === ' ' || e.code === 'Space') {
        // Hold space to pan
        e.preventDefault();
        setPanMode(true);
      } else if (e.key === '0') {
        // Reset zoom
        e.preventDefault();
        setZoom(1);
        setPan({ x: 0, y: 0 });
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setZoom((z) => Math.min(MAX_ZOOM, z * 1.2));
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        setZoom((z) => {
          const next = Math.max(MIN_ZOOM, z / 1.2);
          if (next === 1) setPan({ x: 0, y: 0 });
          return next;
        });
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === ' ' || e.code === 'Space') {
        setPanMode(false);
      }
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [onClose, handleUndoApply]);

  // ── Render ───────────────────────────────────────────────────

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 800;

  return (
    <div className="absolute inset-0 flex flex-col" style={{ background: '#050505' }}>
      {/* Mobile-banner — healing fungerar bäst på desktop */}
      {isMobile ? (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[11px] rounded-full">
          Healing tool fungerar bäst på desktop
        </div>
      ) : null}

      {/* Stage — pointer-handlers ligger HÄR (täcker hela viewporten) så att
          panning + ritning funkar även när man rör sig utanför 1×-bilden. */}
      <div
        ref={containerRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        style={{
          minHeight: 0,
          touchAction: 'none',
          cursor:
            stage === 'ready'
              ? panMode
                ? panDragRef.current.pointerId !== null
                  ? 'grabbing'
                  : 'grab'
                : 'none'
              : 'default',
        }}
      >
        {stage === 'loading' ? (
          <div className="flex flex-col items-center gap-3 text-loupe-text-2 text-sm">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading editor…</span>
            <span className="text-[10px] text-loupe-text-3 tracking-[0.15em] uppercase">
              First load downloads OpenCV.js (~8 MB)
            </span>
          </div>
        ) : stage === 'error' ? (
          <div className="flex flex-col items-center gap-3 text-loupe-text-2 text-sm">
            <span className="text-red-400">Failed to load editor</span>
            <span className="text-[11px] text-loupe-text-3">{errorMsg}</span>
            <button
              onClick={onClose}
              className="mt-2 px-3 py-1.5 text-[11px] tracking-wide uppercase rounded-md border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text"
            >
              Close
            </button>
          </div>
        ) : (
          <div
            className="relative"
            style={{
              width: displaySize.w,
              height: displaySize.h,
            }}
          >
            {/*
              Inre wrapper får zoom + pan via CSS transform. Cursor-canvas ligger
              UTANFÖR transformen så att brush-cirkeln alltid är i CSS-pixlar.
            */}
            <div
              className="absolute inset-0"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition:
                  panDragRef.current.pointerId !== null ? 'none' : 'transform 80ms linear',
              }}
            >
              {/* Source canvas — bilden + healade pixlar */}
              <canvas
                ref={sourceCanvasRef}
                className="absolute inset-0 w-full h-full rounded shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)]"
                style={{
                  width: '100%',
                  height: '100%',
                  imageRendering: 'auto',
                  visibility: showOriginal ? 'hidden' : 'visible',
                }}
              />
              {/* Original-image (visas bara vid F-toggle) */}
              {showOriginal && originalImage ? (
                <img
                  src={src}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full pointer-events-none rounded shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)]"
                  style={{ width: '100%', height: '100%' }}
                />
              ) : null}
              {/* Mask overlay — visar markerade områden i rött */}
              {!showOriginal ? (
                <canvas
                  ref={overlayCanvasRef}
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: '100%', height: '100%' }}
                />
              ) : null}
            </div>
          </div>
        )}

        {/* Cursor-canvas — täcker HELA stage:n så brush syns även när
            musen är utanför 1×-bilden (vid utzoom). Helt CSS-pixlar. */}
        {stage === 'ready' && !panMode ? (
          <canvas
            ref={cursorCanvasRef}
            className="absolute inset-0 pointer-events-none z-20"
            style={{ width: '100%', height: '100%' }}
          />
        ) : null}

        {/* Inpainting-overlay (medan cv kör) */}
        {stage === 'inpainting' || stage === 'saving' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-30">
            <div className="flex items-center gap-2 px-3.5 py-2 bg-loupe-bg-2/95 border border-loupe-border-2 rounded-full text-sm text-loupe-text">
              <Loader2 className="w-4 h-4 animate-spin" />
              {stage === 'inpainting' ? 'Healing…' : 'Saving…'}
            </div>
          </div>
        ) : null}
      </div>

      {/* Bottom toolbar */}
      <div
        className={[
          'flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4',
          'px-4 sm:px-6 py-3 border-t border-loupe-border-2 bg-loupe-bg-2/95 backdrop-blur-xl',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center gap-2 text-loupe-text font-medium text-sm">
          <Brush className="w-4 h-4 text-henrik" />
          <span className="tracking-wide">Healing</span>
          {undoStackRef.current.length > 0 ? (
            <span className="text-[10px] tracking-[0.15em] uppercase text-emerald-400/80 ml-1">
              {undoStackRef.current.length} healed
            </span>
          ) : null}
        </div>

        {/* Brush size control */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 whitespace-nowrap">
            Brush
          </span>
          <input
            type="range"
            min={MIN_BRUSH}
            max={MAX_BRUSH}
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
            disabled={stage !== 'ready'}
            className="flex-1 max-w-[200px] accent-henrik h-1"
          />
          <span className="text-[11px] tabular-nums text-loupe-text-2 w-8 text-right">
            {brushSize}px
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 justify-end flex-wrap">
          {/* Zoom indicator + reset (visas bara när zoomad) */}
          {zoom !== 1 ? (
            <button
              type="button"
              onClick={handleResetZoom}
              title="Reset zoom (0)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] min-h-[32px] rounded-md transition-colors border tracking-wide bg-loupe-bg-3 border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text"
            >
              <span className="tabular-nums">{Math.round(zoom * 100)}%</span>
              <X className="w-3 h-3 opacity-70" />
            </button>
          ) : null}
          <ToolbarButton
            label={showOriginal ? 'Edited' : 'Original'}
            icon={<Eye className="w-3.5 h-3.5" />}
            onClick={() => setShowOriginal((s) => !s)}
            disabled={stage !== 'ready'}
            title="Toggle before/after (F)"
          />
          <ToolbarButton
            label="Undo"
            icon={<RotateCcw className="w-3.5 h-3.5" />}
            onClick={handleUndoApply}
            disabled={stage !== 'ready' || undoStackRef.current.length === 0}
            title="Undo last heal (Cmd+Z)"
          />
          <div className="w-px h-6 bg-loupe-border-2 mx-1" />
          <ToolbarButton
            label="Cancel"
            icon={<X className="w-3.5 h-3.5" />}
            onClick={onClose}
            disabled={stage === 'inpainting' || stage === 'saving'}
            title="Discard edits (Esc)"
          />
          <ToolbarButton
            label="Save"
            icon={<Save className="w-3.5 h-3.5" />}
            onClick={handleSave}
            disabled={
              stage !== 'ready' ||
              (undoStackRef.current.length === 0 && strokes.length === 0)
            }
            primary
            title="Save as new version"
          />
        </div>
      </div>

      {/* Error toast */}
      {errorMsg && stage !== 'error' ? (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 bg-red-500/15 border border-red-500/40 text-red-200 text-[11px] rounded-full max-w-[80%]">
          {errorMsg}
        </div>
      ) : null}
    </div>
  );
}

// ─── ToolbarButton ─────────────────────────────────────────────

interface ToolbarButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  title?: string;
}

function ToolbarButton({
  label,
  icon,
  onClick,
  disabled,
  primary,
  title,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        'flex items-center gap-1.5 px-3 py-1.5 text-[11px] min-h-[32px] rounded-md transition-colors border tracking-wide',
        primary
          ? 'bg-henrik/15 border-henrik/40 text-henrik hover:bg-henrik/25 disabled:opacity-30 disabled:hover:bg-henrik/15'
          : 'bg-loupe-bg-3 border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text hover:border-loupe-text-3 disabled:opacity-30 disabled:hover:text-loupe-text-2',
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
